import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  clearUserCartItems,
  deleteUserCartItem,
  loadUserCart,
  updateUserCartItemQuantity,
  upsertUserCartItem,
} from "../services/cart";

export const CartContext = createContext(null);

const normalizeCartItem = (product) => ({
  id: product.id,
  productId: product.productId || product.id,
  productSlug: product.productSlug || "",
  name: product.name || "",
  selectedSize: product.selectedSize || product.optionLabel || "",
  unitPrice: Number(product.unitPrice ?? product.price) || 0,
  optionLabel: product.selectedSize || product.optionLabel || "",
  price: Number(product.unitPrice ?? product.price) || 0,
  image: product.image || "",
  quantity: Math.max(1, Number(product.quantity) || 1),
});

const GUEST_CART_STORAGE_KEY = "zea-mays-guest-cart";

const readGuestCart = () => {
  try {
    const storedCart = window.localStorage.getItem(GUEST_CART_STORAGE_KEY);
    const parsedCart = storedCart ? JSON.parse(storedCart) : [];
    return Array.isArray(parsedCart) ? parsedCart.map(normalizeCartItem) : [];
  } catch (_error) {
    return [];
  }
};

const writeGuestCart = (items) => {
  try {
    if (items.length > 0) {
      window.localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
    } else {
      window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
    }
  } catch (_error) {
    // The in-memory cart remains usable if browser storage is unavailable.
  }
};

const mergeCartItems = (baseItems, incomingItems) => {
  const merged = [...baseItems];

  incomingItems.forEach((item) => {
    const normalizedItem = normalizeCartItem(item);
    const existingIndex = merged.findIndex((entry) => entry.productId === normalizedItem.productId && entry.selectedSize === normalizedItem.selectedSize);

    if (existingIndex >= 0) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...normalizedItem,
        quantity: merged[existingIndex].quantity + normalizedItem.quantity,
      };
      return;
    }

    merged.push(normalizedItem);
  });

  return merged;
};

export function CartProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const guestCartRef = useRef([]);

  const syncCartWithSupabase = async () => {
    if (!user) {
      const guestCart = readGuestCart();
      guestCartRef.current = guestCart;
      setCart(guestCart);
      setErrorMessage("");
      return guestCart;
    }

    setSyncing(true);

    try {
      const remoteCart = await loadUserCart();
      setCart(remoteCart);
      setErrorMessage("");
      return remoteCart;
    } catch (error) {
      console.error("Unable to sync cart with Supabase:", error);
      setErrorMessage(error.message || "Unable to sync cart.");
      throw error;
    } finally {
      setSyncing(false);
    }
  };

  const replaceCart = (nextCart) => {
    const normalizedCart = Array.isArray(nextCart) ? nextCart.map(normalizeCartItem) : [];
    guestCartRef.current = normalizedCart;
    setCart(normalizedCart);
    if (!user) {
      writeGuestCart(normalizedCart);
    }
    setErrorMessage("");
  };

  useEffect(() => {
    let isMounted = true;

    const syncAuthenticatedCart = async () => {
      if (authLoading) {
        return;
      }

      if (!user) {
        const guestCart = readGuestCart();
        guestCartRef.current = guestCart;
        if (isMounted) {
          setCart(guestCart);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const remoteCart = await loadUserCart();
        const pendingGuestCart = guestCartRef.current.length > 0 ? guestCartRef.current : readGuestCart();
        guestCartRef.current = pendingGuestCart;
        const mergedCart = mergeCartItems(remoteCart, pendingGuestCart);

        if (!isMounted) {
          return;
        }

        setCart(mergedCart);

        if (pendingGuestCart.length > 0) {
          setSyncing(true);
          for (const item of pendingGuestCart) {
            await upsertUserCartItem(item);
          }
          guestCartRef.current = [];
          writeGuestCart([]);
          const syncedCart = await loadUserCart();
          if (isMounted) {
            setCart(syncedCart);
          }
        }
        setErrorMessage("");
      } catch (error) {
        console.error("Unable to load cart:", error);
        setErrorMessage(error.message || "Unable to load cart.");
        if (isMounted) {
          setCart([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setSyncing(false);
        }
      }
    };

    syncAuthenticatedCart();

    return () => {
      isMounted = false;
    };
  }, [authLoading, user]);

  const persistItem = async (nextItem) => {
    if (!nextItem) {
      return;
    }

    setSyncing(true);
    setErrorMessage("");

    try {
      const savedItem = await upsertUserCartItem(nextItem);
      setCart((prevCart) => {
        const existingIndex = prevCart.findIndex(
          (item) =>
            item.id === savedItem.id ||
            (item.productId === savedItem.productId && item.selectedSize === savedItem.selectedSize)
        );

        if (existingIndex < 0) {
          return [...prevCart, savedItem];
        }

        return prevCart.map((item) => (item.id === savedItem.id ? savedItem : item));
      });
    } catch (error) {
      console.error("Unable to sync cart item:", error);
      setErrorMessage(error.message || "Unable to save cart item.");
      throw error;
    } finally {
      setSyncing(false);
    }
  };

  const addToCart = async (product) => {
    const normalizedProduct = normalizeCartItem(product);

    if (!user) {
      const nextGuestCart = mergeCartItems(guestCartRef.current, [normalizedProduct]);
      guestCartRef.current = nextGuestCart;
      setCart(nextGuestCart);
      writeGuestCart(nextGuestCart);
      setErrorMessage("");
      return;
    }

    try {
      await persistItem(normalizedProduct);
    } catch (error) {
      window.alert(error.message || "Unable to save cart item.");
      return;
    }
  };

  const removeFromCart = async (id) => {
    const previousCart = cart;
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
    setErrorMessage("");

    if (!user) {
      guestCartRef.current = guestCartRef.current.filter((item) => item.id !== id);
      writeGuestCart(guestCartRef.current);
      return;
    }

    setSyncing(true);

    try {
      await deleteUserCartItem(id);
    } catch (error) {
      console.error("Unable to remove cart item:", error);
      setCart(previousCart);
      setErrorMessage(error.message || "Unable to remove cart item.");
    } finally {
      setSyncing(false);
    }
  };

  const updateQuantity = async (id, quantity) => {
    const nextQuantity = Math.max(0, Number(quantity) || 0);

    if (nextQuantity <= 0) {
      await removeFromCart(id);
      return;
    }

    const previousCart = cart;
    setCart((prevCart) => prevCart.map((item) => (item.id === id ? { ...item, quantity: nextQuantity } : item)));
    setErrorMessage("");

    if (!user) {
      guestCartRef.current = guestCartRef.current.map((item) =>
        item.id === id ? { ...item, quantity: nextQuantity } : item
      );
      writeGuestCart(guestCartRef.current);
      return;
    }

    setSyncing(true);

    try {
      const savedItem = await updateUserCartItemQuantity(id, nextQuantity);
      setCart((prevCart) => prevCart.map((item) => (item.id === savedItem.id ? savedItem : item)));
    } catch (error) {
      console.error("Unable to update cart quantity:", error);
      setCart(previousCart);
      setErrorMessage(error.message || "Unable to update cart quantity.");
    } finally {
      setSyncing(false);
    }
  };

  const clearCart = async (itemsToClear = null) => {
    const normalizedItemsToClear = Array.isArray(itemsToClear) ? itemsToClear : null;
    const sourceItems =
      normalizedItemsToClear && normalizedItemsToClear.some((item) => item?.id)
        ? normalizedItemsToClear
        : cart;

    const targetIds = sourceItems
      .map((item) => item.id)
      .filter(Boolean);

    if (!targetIds.length) {
      return;
    }

    setCart((prevCart) => prevCart.filter((item) => !targetIds.includes(item.id)));
    setErrorMessage("");

    if (!user) {
      guestCartRef.current = guestCartRef.current.filter((item) => !targetIds.includes(item.id));
      writeGuestCart(guestCartRef.current);
      return;
    }

    setSyncing(true);

    try {
      await clearUserCartItems(targetIds);
    } catch (error) {
      console.error("Unable to clear cart items:", error);
      setErrorMessage(error.message || "Unable to clear cart items.");
    } finally {
      setSyncing(false);
    }
  };

  const cartSubtotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart]
  );
  const cartCount = useMemo(
    () => cart.reduce((count, item) => count + item.quantity, 0),
    [cart]
  );

  const value = {
    cart,
    cartCount,
    cartSubtotal,
    loading,
    syncing,
    errorMessage,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    replaceCart,
    syncCartWithSupabase,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};
